import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { authService } from '../features/user/userService';
import { getUserCart } from '../features/user/userSlice';
import { base_url } from '../utils/axiosConfig';
import axios from 'axios';
import { FaArrowLeft, FaBox, FaCheckCircle, FaTruck, FaTimesCircle, FaUndo, FaShoppingCart, FaMapMarkerAlt, FaCreditCard, FaClipboardList } from 'react-icons/fa';
import { MdCancel, MdRepeat } from 'react-icons/md';

/* ─── Status config ─────────────────────────────────────── */
const STATUS_CONFIG = {
  Pending:    { color: '#f59e0b', bg: '#fef3c7', icon: <FaBox /> },
  Processing: { color: '#3b82f6', bg: '#dbeafe', icon: <FaBox /> },
  Shipped:    { color: '#8b5cf6', bg: '#ede9fe', icon: <FaTruck /> },
  Delivered:  { color: '#10b981', bg: '#d1fae5', icon: <FaCheckCircle /> },
  Cancelled:  { color: '#ef4444', bg: '#fee2e2', icon: <FaTimesCircle /> },
  Return:     { color: '#f97316', bg: '#ffedd5', icon: <FaUndo /> },
};

/* ─── TrackOrder delivery stages ────────────────────────── */
const TRACK_STAGES = ['Ordered', 'In Transit', 'Out for Delivery', 'Delivered'];
const TRACK_STAGE_ICONS = {
  'Ordered':          '🏪',
  'In Transit':       '🚚',
  'Out for Delivery': '🛵',
  'Delivered':        '✅',
};
const getTrackStepIndex = (status) => {
  const idx = TRACK_STAGES.indexOf(status);
  return idx === -1 ? 0 : idx;
};
const calcTrackProgress = (status) =>
  ((getTrackStepIndex(status) + 1) / TRACK_STAGES.length) * 100;

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [repeating, setRepeating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [trackedOrder, setTrackedOrder] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await authService.getOrderById(id);
        setOrder(response.order);
        // Auto-fetch live tracking using the order's tracking number
        if (response.order?.trackingNumber) {
          try {
            const token = JSON.parse(localStorage.getItem('customer'))?.token;
            const trackRes = await axios.post(
              `${base_url}user/trackorder`,
              { trackingNumber: response.order.trackingNumber },
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            if (trackRes.data) setTrackedOrder(trackRes.data);
          } catch {
            // tracking fetch is best-effort, silently ignore
          }
        }
      } catch {
        toast.error('Failed to fetch order details');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const handleRepeatOrder = async () => {
    setRepeating(true);
    try {
      if (isAuthenticated) {
        for (const item of order.orderItems) {
          await authService.addToCart({
            ProductId: item.product._id,
            quantity: item.quantity,
            price: item.price,
          });
        }
        await dispatch(getUserCart());
        toast.success('Items added to cart');
      } else {
        let localCart = JSON.parse(localStorage.getItem('cart')) || [];
        order.orderItems.forEach((item) => {
          const index = localCart.findIndex((i) => i.ProductId._id === item.product._id);
          if (index >= 0) localCart[index].quantity += item.quantity;
          else localCart.push({ ProductId: item.product, price: item.price, quantity: item.quantity });
        });
        localStorage.setItem('cart', JSON.stringify(localCart));
        toast.success('Items added to local cart');
      }
      navigate('/cart');
    } catch {
      toast.error('Failed to add items to cart');
    } finally {
      setRepeating(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) return toast.error('Provide a cancellation reason');
    setCancelling(true);
    try {
      const token = JSON.parse(localStorage.getItem('customer'))?.token;
      if (!token) throw new Error('Authentication required');
      const res = await axios.put(
        `${base_url}user/cancelorder/${id}`,
        { reason: cancelReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setOrder({ ...order, orderStatus: 'Cancelled', cancellationReason: cancelReason });
        toast.success('Order cancelled');
        setCancelDialogOpen(false);
        setCancelReason('');
      } else toast.error(res.data.message);
    } catch (err) {
      toast.error(err.message || 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div style={styles.loaderWrap}>
        <div style={styles.spinner} />
        <p style={{ color: '#777', marginTop: 12, fontFamily: 'Rubik, sans-serif' }}>Fetching order details…</p>
      </div>
    );
  }

  /* ── Not found ── */
  if (!order) {
    return (
      <div style={styles.loaderWrap}>
        <FaBox size={48} color="#ccc" />
        <p style={{ color: '#555', marginTop: 12 }}>Order not found.</p>
        <button style={styles.primaryBtn} onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[order.orderStatus] || STATUS_CONFIG.Pending;
  const isCancelledOrReturn = order.orderStatus === 'Cancelled' || order.orderStatus === 'Return';
  // Use live tracking status if available, else fall back to order status for stepper
  const trackStatus = trackedOrder?.orderStatus || order.orderStatus;

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* ── Back button ── */}
        <button style={styles.backBtn} onClick={() => navigate(-1)}>
          <FaArrowLeft size={14} style={{ marginRight: 6 }} /> Back to Orders
        </button>

        {/* ── Page title ── */}
        <div style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>Order Details</h1>
          <span style={{ ...styles.statusBadge, backgroundColor: statusCfg.bg, color: statusCfg.color }}>
            {statusCfg.icon}&nbsp;&nbsp;{order.orderStatus}
          </span>
        </div>

        {/* ── Layout grid ── */}
        <div style={styles.grid}>

          {/* ─── LEFT column ─────────────────────────────── */}
          <div style={styles.leftCol}>

            {/* Order Summary card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <FaClipboardList style={styles.cardHeaderIcon} />
                <span>Order Summary</span>
              </div>
              <div style={styles.cardBody}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Tracking Number</span>
                  <span style={styles.trackingNumber}>{order.trackingNumber}</span>
                </div>
                <div style={styles.divider} />
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Order Date</span>
                  <span style={styles.infoValue}>{new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Order Time</span>
                  <span style={styles.infoValue}>{new Date(order.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Customer Email</span>
                  <span style={styles.infoValue}>{order.user?.email}</span>
                </div>
                {order.cancellationReason && (
                  <div style={styles.alertBox('#fee2e2', '#b91c1c')}>
                    <strong>Cancellation Reason:</strong> {order.cancellationReason}
                  </div>
                )}
                {order.orderStatus === 'Return' && order.returnReason && (
                  <div style={styles.alertBox('#ffedd5', '#c2410c')}>
                    <strong>Return Reason:</strong> {order.returnReason}
                  </div>
                )}
              </div>
            </div>

            {/* ── Order Progress stepper (from TrackOrder) ── */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <FaTruck style={styles.cardHeaderIcon} />
                <span>Order Progress</span>
                {trackedOrder && (
                  <span style={styles.liveChip}>● Live</span>
                )}
              </div>
              <div style={{ padding: '18px 18px 14px' }}>
                {isCancelledOrReturn ? (
                  <div style={styles.cancelledBox}>
                    <FaTimesCircle color="#ef4444" size={20} />
                    <div>
                      <p style={{ fontWeight: 700, color: '#ef4444', margin: 0, fontSize: 14 }}>
                        Order {order.orderStatus}
                      </p>
                      {order.cancellationReason && (
                        <p style={{ fontSize: 12, color: '#b91c1c', margin: '3px 0 0' }}>Reason: {order.cancellationReason}</p>
                      )}
                      {order.returnReason && (
                        <p style={{ fontSize: 12, color: '#c2410c', margin: '3px 0 0' }}>Reason: {order.returnReason}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={styles.stepperWrap}>
                    {/* Background track */}
                    <div style={styles.stepperBgLine} />
                    {/* Orange progress fill */}
                    <div style={{ ...styles.stepperFill, width: `${calcTrackProgress(trackStatus)}%` }} />
                    {/* Steps */}
                    {TRACK_STAGES.map((stage, idx) => {
                      const completed = idx <= getTrackStepIndex(trackStatus);
                      const active    = idx === getTrackStepIndex(trackStatus);
                      return (
                        <div key={stage} style={styles.stepItem}>
                          <div style={{
                            ...styles.stepCircle,
                            backgroundColor: completed ? (active ? '#ff6701' : '#10b981') : '#e5e7eb',
                            boxShadow: active ? '0 0 0 5px rgba(255,103,1,0.18)' : 'none',
                            transform: active ? 'scale(1.18)' : 'scale(1)',
                          }}>
                            <span style={{ fontSize: 18 }}>{TRACK_STAGE_ICONS[stage]}</span>
                          </div>
                          <p style={{
                            ...styles.stepLabel,
                            color: completed ? '#232f3e' : '#aaa',
                            fontWeight: active ? 700 : 500,
                          }}>{stage}</p>
                          {active && <p style={styles.stepCurrent}>Current</p>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Payment & Delivery card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <FaCreditCard style={styles.cardHeaderIcon} />
                <span>Payment & Delivery</span>
              </div>
              <div style={styles.cardBody}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Payment Method</span>
                  <span style={{ ...styles.infoValue, ...styles.paymentChip }}>
                    {order.paymentInfo?.cashOndelivery ? '💵 Cash on Delivery' : '💳 Online Payment'}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Delivery Instructions</span>
                  <span style={styles.infoValue}>{order.deliveryInstruction || 'None'}</span>
                </div>
              </div>
            </div>

            {/* Shipping address card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <FaMapMarkerAlt style={styles.cardHeaderIcon} />
                <span>Shipping Address</span>
              </div>
              <div style={styles.cardBody}>
                <p style={styles.addressLine}>{order.shippingInfo?.address}</p>
                {order.shippingInfo?.landmark && (
                  <p style={{ ...styles.addressLine, color: '#777' }}>Near: {order.shippingInfo.landmark}</p>
                )}
                <p style={styles.addressLine}>
                  {order.shippingInfo?.city}, {order.shippingInfo?.state}
                </p>
                <p style={{ ...styles.addressLine, fontWeight: 600 }}>PIN: {order.shippingInfo?.pincode}</p>
              </div>
            </div>
          </div>

          {/* ─── RIGHT column ────────────────────────────── */}
          <div style={styles.rightCol}>

            {/* Order Items card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <FaShoppingCart style={styles.cardHeaderIcon} />
                <span>Items ({order.orderItems?.length})</span>
              </div>
              <div style={{ padding: '0 4px' }}>
                {order.orderItems?.map((item, idx) => (
                  <div key={item._id} style={{ ...styles.productRow, borderBottom: idx < order.orderItems.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                    <div style={styles.productImgWrap}>
                      <img
                        src={item.product?.images?.[0]?.url ? `${base_url}${item.product.images[0].url}` : '/default.jpg'}
                        alt={item.product?.title || 'Product'}
                        style={styles.productImg}
                      />
                    </div>
                    <div style={styles.productInfo}>
                      <p style={styles.productTitle}>{item.product?.title || 'Product'}</p>
                      <p style={styles.productMeta}>Qty: <strong>{item.quantity}</strong></p>
                    </div>
                    <div style={styles.productPrice}>
                      ₹{(item.price * item.quantity)?.toFixed(2) || 0}
                    </div>
                  </div>
                ))}
              </div>

              {/* Price breakdown */}
              <div style={styles.priceBox}>
                <div style={styles.priceRow}>
                  <span style={{ color: '#777' }}>Subtotal</span>
                  <span>₹{order.totalPrice?.toFixed(2)}</span>
                </div>
                <div style={styles.priceRow}>
                  <span style={{ color: '#777' }}>Delivery</span>
                  <span style={{ color: '#10b981' }}>FREE</span>
                </div>
                <div style={{ ...styles.divider, margin: '10px 0' }} />
                <div style={{ ...styles.priceRow, fontWeight: 700, fontSize: 18 }}>
                  <span>Total</span>
                  <span style={{ color: '#ff6701' }}>₹{order.totalPrice?.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div style={styles.actionsCard}>
              <button
                style={{ ...styles.primaryBtn, opacity: (repeating || order.orderStatus === 'Cancelled') ? 0.6 : 1 }}
                onClick={handleRepeatOrder}
                disabled={repeating || order.orderStatus === 'Cancelled'}
              >
                <MdRepeat size={16} style={{ marginRight: 6 }} />
                {repeating ? 'Adding to cart…' : 'Repeat Order'}
              </button>

              {!isCancelledOrReturn && (
                <button
                  style={styles.dangerBtn}
                  onClick={() => setCancelDialogOpen(true)}
                >
                  <MdCancel size={16} style={{ marginRight: 6 }} />
                  Cancel Order
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Cancel Dialog Overlay ── */}
      {cancelDialogOpen && (
        <div style={styles.overlay} onClick={() => !cancelling && setCancelDialogOpen(false)}>
          <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div style={styles.dialogHeader}>
              <MdCancel size={22} color="#ef4444" style={{ marginRight: 8 }} />
              <h2 style={styles.dialogTitle}>Cancel Order</h2>
            </div>
            <p style={styles.dialogSubtitle}>Please tell us why you want to cancel this order.</p>
            <label style={styles.inputLabel}>Reason for Cancellation</label>
            <textarea
              style={styles.textarea}
              placeholder="e.g. Found a better price, changed my mind…"
              rows={4}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              disabled={cancelling}
              autoFocus
            />
            <div style={styles.dialogActions}>
              <button
                style={styles.ghostBtn}
                onClick={() => setCancelDialogOpen(false)}
                disabled={cancelling}
              >
                Keep Order
              </button>
              <button
                style={{ ...styles.dangerBtn, opacity: (!cancelReason.trim() || cancelling) ? 0.55 : 1 }}
                onClick={handleCancelOrder}
                disabled={!cancelReason.trim() || cancelling}
              >
                {cancelling ? 'Cancelling…' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────── Inline styles ─────────────────────────────── */
const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f7',
    fontFamily: '"Rubik", "Poppins", sans-serif',
    paddingBottom: 60,
  },
  container: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '24px 16px',
  },
  loaderWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '70vh',
    fontFamily: '"Rubik", sans-serif',
  },
  spinner: {
    width: 44,
    height: 44,
    border: '4px solid #f0f0f0',
    borderTop: '4px solid #ff6701',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'none',
    border: '1.5px solid #e0e0e0',
    borderRadius: 8,
    padding: '7px 16px',
    fontSize: 13,
    fontWeight: 500,
    color: '#444',
    cursor: 'pointer',
    marginBottom: 20,
    fontFamily: 'inherit',
    transition: 'all 0.2s',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 12,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1c1c1b',
    margin: 0,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 16px',
    borderRadius: 20,
    fontWeight: 600,
    fontSize: 13,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 20,
    alignItems: 'start',
  },
  leftCol: { display: 'flex', flexDirection: 'column', gap: 16 },
  rightCol: { display: 'flex', flexDirection: 'column', gap: 16 },
  card: {
    background: '#fff',
    borderRadius: 14,
    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 18px',
    borderBottom: '1px solid #f0f0f0',
    fontWeight: 700,
    fontSize: 14,
    color: '#232f3e',
    backgroundColor: '#fafafa',
  },
  cardHeaderIcon: { color: '#ff6701', fontSize: 16 },
  cardBody: { padding: '14px 18px' },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    flexWrap: 'wrap',
    gap: 4,
  },
  infoLabel: { fontSize: 13, color: '#888', fontWeight: 500 },
  infoValue: { fontSize: 13, color: '#333', fontWeight: 600, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' },
  trackingNumber: {
    fontSize: 13,
    fontWeight: 700,
    color: '#ff6701',
    letterSpacing: 0.5,
    fontFamily: 'monospace',
  },
  divider: { height: 1, backgroundColor: '#f0f0f0', margin: '4px 0' },
  alertBox: (bg, text) => ({
    marginTop: 12,
    padding: '10px 14px',
    borderRadius: 8,
    backgroundColor: bg,
    color: text,
    fontSize: 13,
    lineHeight: 1.5,
    borderLeft: `3px solid ${text}`,
  }),
  paymentChip: {
    backgroundColor: '#f0fdf4',
    color: '#166534',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
  },
  addressLine: { fontSize: 13, color: '#444', marginBottom: 4 },
  /* ─ Live chip ─ */
  liveChip: {
    marginLeft: 'auto', fontSize: 11, fontWeight: 700,
    color: '#10b981', backgroundColor: '#d1fae5',
    padding: '2px 8px', borderRadius: 20,
  },
  /* ─ Cancelled box ─ */
  cancelledBox: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: '12px 16px', borderRadius: 10,
    backgroundColor: '#fef2f2', border: '1px solid #fecaca',
  },
  /* ─ Horizontal stepper ─ */
  stepperWrap: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    position: 'relative', padding: '16px 8px 8px',
    backgroundColor: '#f9fafb', borderRadius: 12, overflowX: 'auto',
  },
  stepperBgLine: {
    position: 'absolute', top: 36, left: 44, right: 44,
    height: 4, backgroundColor: '#e5e7eb', zIndex: 1,
  },
  stepperFill: {
    position: 'absolute', top: 36, left: 44,
    height: 4, backgroundColor: '#ff6701', zIndex: 2,
    transition: 'width 0.5s ease',
    maxWidth: 'calc(100% - 88px)',
  },
  stepItem: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    zIndex: 3, minWidth: 70, gap: 4,
  },
  stepCircle: {
    width: 48, height: 48, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.3s ease', marginBottom: 2,
    backgroundColor: '#e5e7eb',
  },
  stepLabel: {
    fontSize: 11, textAlign: 'center', margin: 0,
    lineHeight: 1.3, maxWidth: 70,
  },
  stepCurrent: {
    fontSize: 10, color: '#ff6701', fontWeight: 700, margin: 0,
  },
  /* Product rows */
  productRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 18px',
  },
  productImgWrap: {
    width: 64, height: 64, flexShrink: 0,
    borderRadius: 10, overflow: 'hidden',
    backgroundColor: '#f5f5f7',
    border: '1px solid #eee',
  },
  productImg: { width: '100%', height: '100%', objectFit: 'cover' },
  productInfo: { flex: 1, minWidth: 0 },
  productTitle: { fontSize: 13, fontWeight: 600, color: '#1c1c1b', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  productMeta: { fontSize: 12, color: '#888', margin: 0 },
  productPrice: { fontSize: 14, fontWeight: 700, color: '#232f3e', whiteSpace: 'nowrap' },
  /* Price box */
  priceBox: { padding: '14px 18px', borderTop: '1px solid #f0f0f0', backgroundColor: '#fafafa' },
  priceRow: { display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 500, marginBottom: 6 },
  /* Actions */
  actionsCard: {
    background: '#fff',
    borderRadius: 14,
    padding: 18,
    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '100%', padding: '12px 20px',
    backgroundColor: '#232f3e', color: '#fff',
    border: 'none', borderRadius: 10,
    fontWeight: 600, fontSize: 14, cursor: 'pointer',
    fontFamily: 'inherit', transition: 'background 0.2s',
  },
  dangerBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '100%', padding: '12px 20px',
    backgroundColor: '#fff', color: '#ef4444',
    border: '1.5px solid #ef4444', borderRadius: 10,
    fontWeight: 600, fontSize: 14, cursor: 'pointer',
    fontFamily: 'inherit', transition: 'all 0.2s',
  },
  ghostBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '11px 20px', flex: 1,
    backgroundColor: '#f5f5f7', color: '#555',
    border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontWeight: 600, fontSize: 14, cursor: 'pointer',
    fontFamily: 'inherit',
  },
  /* Dialog */
  overlay: {
    position: 'fixed', inset: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: 16,
  },
  dialog: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: '28px 24px',
    width: '100%', maxWidth: 460,
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    fontFamily: '"Rubik", sans-serif',
  },
  dialogHeader: { display: 'flex', alignItems: 'center', marginBottom: 8 },
  dialogTitle: { fontSize: 18, fontWeight: 700, color: '#1c1c1b', margin: 0 },
  dialogSubtitle: { fontSize: 13, color: '#888', marginBottom: 20 },
  inputLabel: { display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 8 },
  textarea: {
    width: '100%', padding: '12px 14px',
    border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontSize: 13, fontFamily: 'inherit', color: '#333',
    resize: 'vertical', outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  dialogActions: { display: 'flex', gap: 10, marginTop: 18 },
};

export default OrderDetails;
